import { ReactNode, useState } from 'react';

import {
  CollegeProgram,
  CollegePrograms,
  DonutChart,
  formatCurrency,
  HorizontalMenuTabs,
  ProgramCredentialLevel,
  PROGRAM_CREDENTIAL_LEVELS,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LineInfoIcon } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/LineInfoIcon';

import { Box, Chip, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';

import { ProgramEarningsRow, ProgramEarningsTable } from './ProgramEarningsTable';
import {
  computeDegreeMix,
  ProgramEarningsSort,
  computeEarningsStats,
  DegreeMix,
  EarningsStats,
  hasCurrentAwards,
  programsAtLevel,
  sortEarningsRows,
  visibleLevels,
} from './programStats';
import { useAgentContext } from './WhatStudentsStudy.agentContext';

const STRINGS = appStrings.modules.college.whatStudentsStudy;

const LEVEL_LABELS: Record<ProgramCredentialLevel, string> = {
  [PROGRAM_CREDENTIAL_LEVELS.BACHELORS]: STRINGS.levels.bachelors,
  [PROGRAM_CREDENTIAL_LEVELS.MASTERS]: STRINGS.levels.masters,
};

const DEFAULT_SORT: ProgramEarningsSort = { key: 'earnings', direction: 'desc' };
const INFO_ICON_COLOR = 'Colors/Foreground/fg-quinary';
const INFO_ICON_NAME = 'HelpCircle';

const formatShare = (share: number): string => `${(share * 100).toFixed(1)}%`;

const formatRange = (low: number, high: number): string =>
  low === high ? formatCurrency(low) : `${formatCurrency(low)} – ${formatCurrency(high)}`;

type WhatStudentsStudyProps = Readonly<{
  schoolName: string;
  data: CollegePrograms;
}>;

export const WhatStudentsStudy = ({ schoolName, data }: WhatStudentsStudyProps) => {
  const levels = visibleLevels(data.programs);
  const [selectedLevel, setSelectedLevel] = useState<ProgramCredentialLevel | null>(null);
  const [sort, setSort] = useState<ProgramEarningsSort>(DEFAULT_SORT);

  const level = selectedLevel != null && levels.includes(selectedLevel) ? selectedLevel : levels[0];
  const levelLabel = level == null ? '' : LEVEL_LABELS[level];
  const levelPrograms = level == null ? [] : programsAtLevel(data.programs, level);

  const mix = computeDegreeMix(levelPrograms);
  const stats = computeEarningsStats(levelPrograms);
  const sortedRows = sortEarningsRows(levelPrograms, sort.key, sort.direction);

  useAgentContext(
    schoolName,
    data.release,
    level == null ? null : levelLabel,
    mix,
    stats,
    sortedRows,
  );

  if (level == null) return null;

  const metadata = data.release?.metadata;

  const levelSelector =
    levels.length > 1 ? (
      <HorizontalMenuTabs
        id="college-programs-level"
        variant="filter"
        tabs={levels.map((lvl) => LEVEL_LABELS[lvl])}
        value={levels.indexOf(level)}
        setValue={(index) => setSelectedLevel(levels[index])}
        containerSxOverride={compactTabsSx}
      />
    ) : (
      <Typography variant="text-sm-semibold" sx={tertiaryTextSx}>
        {levelLabel}
      </Typography>
    );

  return (
    <Box sx={sectionSx}>
      <DegreeMixCard
        mix={mix}
        levelLabel={levelLabel}
        levelSelector={levelSelector}
        academicYear={metadata?.awards.latest_academic_year ?? null}
      />

      {stats ? (
        <EarningsBlock
          stats={stats}
          rows={sortedRows}
          sort={sort}
          onSortChange={setSort}
          cohortYears={metadata?.earnings.yr4.cohort_academic_years ?? null}
          dollarYear={metadata?.earnings.yr4.dollar_year ?? null}
        />
      ) : (
        <Typography id="college-programs-no-earnings" variant="text-sm-regular" sx={tertiaryTextSx}>
          {STRINGS.earnings.noEarnings(levelLabel)}
        </Typography>
      )}
    </Box>
  );
};

type DegreeMixCardProps = Readonly<{
  mix: DegreeMix;
  levelLabel: string;
  levelSelector: ReactNode;
  academicYear: string | null;
}>;

const DegreeMixCard = ({ mix, levelLabel, levelSelector, academicYear }: DegreeMixCardProps) => {
  const possessive = levelLabel.toLowerCase();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <Box sx={cardSx}>
      <Box sx={cardTitleRowSx}>
        <Typography variant="text-md-semibold" sx={primaryTextSx}>
          {STRINGS.degreeMix.title}
        </Typography>
        <LineInfoIcon
          idPrefix="college-programs-degree-mix"
          iconColor={INFO_ICON_COLOR}
          iconName={INFO_ICON_NAME}
          info={{
            kind: 'modal',
            modalProps: {
              headerContent: (
                <Typography variant="text-lg-semibold">{STRINGS.degreeMix.info.title}</Typography>
              ),
              bodyContent: (
                <InfoBody
                  definition={STRINGS.degreeMix.info.definition}
                  goodToKnow={STRINGS.degreeMix.info.goodToKnow}
                  bullets={STRINGS.degreeMix.info.bullets(
                    mix.totalAwards.toLocaleString(),
                    possessive,
                    mix.programCount,
                    academicYear,
                  )}
                  source={STRINGS.degreeMix.info.source}
                />
              ),
            },
          }}
        />
        <Box sx={levelSelectorSx}>{levelSelector}</Box>
      </Box>

      <Box sx={donutRowSx}>
        <DonutChart
          variant={{
            type: 'multi',
            segments: mix.categories.map((entry) => ({
              label: entry.category.label,
              value: Math.round(entry.share * 1000) / 10,
              color: entry.category.color,
            })),
          }}
          size={isMobile ? 'small' : 'medium'}
        />

        <Box sx={legendSx(Math.ceil(mix.categories.length / 2))}>
          {mix.categories.map((entry) => (
            <Box key={entry.category.id} sx={legendItemSx}>
              <Box sx={legendSwatchSx(entry.category.color)} />
              <Typography variant="text-xs-semibold" sx={primaryTextSx}>
                {entry.category.legendLabel ?? entry.category.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {(mix.largestCategory || mix.largestProgram) && (
          <Box id="college-programs-degree-mix-highlights" sx={highlightsSx}>
            {mix.largestCategory && (
              <StatCard
                title={STRINGS.degreeMix.largestField}
                value={mix.largestCategory.category.label}
                badge={formatShare(mix.largestCategory.share)}
              />
            )}
            {mix.largestProgram && (
              <StatCard
                title={STRINGS.degreeMix.largestProgram}
                value={mix.largestProgram.program.cip_desc}
                badge={formatShare(mix.largestProgram.share)}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

type EarningsBlockProps = Readonly<{
  stats: EarningsStats;
  rows: readonly CollegeProgram[];
  sort: ProgramEarningsSort;
  onSortChange: (sort: ProgramEarningsSort) => void;
  cohortYears: readonly string[] | null;
  dollarYear: number | null;
}>;

const EarningsBlock = ({
  stats,
  rows,
  sort,
  onSortChange,
  cohortYears,
  dollarYear,
}: EarningsBlockProps) => {
  const tableRows: ProgramEarningsRow[] = rows.map((program) => ({
    id: `${program.credlev}-${program.cip_family_code}`,
    name: program.cip_desc,
    awards: hasCurrentAwards(program) ? program.awards.latest : null,
    earnings: program.earnings.yr4.median ?? 0,
  }));

  return (
    <Box sx={sectionSx}>
      <Box sx={kpiRowSx}>
        <KpiCard
          title={STRINGS.earnings.kpis.median.title}
          value={formatCurrency(stats.median)}
          subtext={STRINGS.earnings.kpis.median.subtext(stats.reportingProgramCount)}
          info={
            <LineInfoIcon
              idPrefix="college-programs-earnings"
              iconColor={INFO_ICON_COLOR}
              iconName={INFO_ICON_NAME}
              info={{
                kind: 'modal',
                modalProps: {
                  headerContent: (
                    <Typography variant="text-lg-semibold">
                      {STRINGS.earnings.info.title}
                    </Typography>
                  ),
                  bodyContent: (
                    <InfoBody
                      definition={STRINGS.earnings.info.definition(cohortYears, dollarYear)}
                      goodToKnow={STRINGS.earnings.info.goodToKnow}
                      bullets={STRINGS.earnings.info.bullets}
                      source={STRINGS.earnings.info.source}
                    />
                  ),
                },
              }}
            />
          }
        />
        <KpiCard
          title={STRINGS.earnings.kpis.iqr.title}
          value={formatRange(stats.iqr.low, stats.iqr.high)}
          subtext={STRINGS.earnings.kpis.iqr.subtext}
        />
        <KpiCard
          title={STRINGS.earnings.kpis.range.title}
          value={formatRange(stats.range.low, stats.range.high)}
          subtext={STRINGS.earnings.kpis.range.subtext(
            stats.lowestProgram.cip_desc,
            stats.highestProgram.cip_desc,
          )}
        />
      </Box>

      <ProgramEarningsTable
        id="college-programs-earnings-table"
        footnote={STRINGS.earnings.coverage(stats.reportingProgramCount, stats.currentProgramCount)}
        rows={tableRows}
        sort={sort}
        onSortChange={onSortChange}
        labels={{
          ...STRINGS.earnings.table,
          missingValue: STRINGS.earnings.missingValue,
          showMore: appStrings.common.button.showMore,
          showLess: appStrings.common.button.showLess,
        }}
      />
    </Box>
  );
};

type StatCardProps = Readonly<{
  title: string;
  value: string;
  badge: string;
}>;

const StatCard = ({ title, value, badge }: StatCardProps) => (
  <Box sx={statCardSx}>
    <Typography variant="text-md-medium" sx={tertiaryTextSx}>
      {title}
    </Typography>
    <Box sx={statValueRowSx}>
      <Typography variant="text-xl-semibold" sx={primaryTextSx}>
        {value}
      </Typography>
      <Chip variant="badge" size="sm" label={badge} sx={statBadgeSx} />
    </Box>
  </Box>
);

type KpiCardProps = Readonly<{
  title: string;
  value: string;
  subtext: string;
  info?: ReactNode;
}>;

const KpiCard = ({ title, value, subtext, info }: KpiCardProps) => (
  <Box sx={kpiCardSx}>
    <Box sx={kpiCardBodySx}>
      <Box sx={cardTitleRowSx}>
        <Typography variant="text-sm-medium" sx={tertiaryTextSx}>
          {title}
        </Typography>
        {info}
      </Box>
      <Typography variant="display-sm-semibold" sx={primaryTextSx}>
        {value}
      </Typography>
    </Box>
    <Box sx={kpiCardFooterSx}>
      <Typography variant="text-sm-regular" sx={kpiCardFooterTextSx}>
        {subtext}
      </Typography>
    </Box>
  </Box>
);

type InfoBodyProps = Readonly<{
  definition: string;
  goodToKnow: string;
  bullets: readonly string[];
  source: string;
}>;

const InfoBody = ({ definition, goodToKnow, bullets, source }: InfoBodyProps) => (
  <Box sx={infoBodySx}>
    <Typography variant="text-sm-regular" sx={primaryTextSx}>
      {definition}
    </Typography>
    <Box>
      <Typography variant="text-sm-semibold" sx={primaryTextSx}>
        {goodToKnow}
      </Typography>
      <Box component="ul" sx={bulletListSx}>
        {bullets.map((item) => (
          <Typography key={item} component="li" variant="text-sm-regular" sx={primaryTextSx}>
            {item}
          </Typography>
        ))}
      </Box>
    </Box>
    <Typography variant="text-xs-regular" sx={tertiaryTextSx}>
      {source}
    </Typography>
  </Box>
);

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  padding: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-2xl'],
  },
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const cardTitleRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
};

const levelSelectorSx: SxProps<Theme> = {
  marginLeft: 'auto',
};

const highlightsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
  gridColumn: '1 / -1',
  '@container (min-width: 600px)': {
    flexDirection: 'row',
  },
  '@container (min-width: 900px)': {
    flexDirection: 'column',
    gridColumn: 'auto',
  },
});

const compactTabsSx: SxProps<Theme> = ({ backrs, typography }) => ({
  position: 'static',
  p: backrs.spacing['spacing-xxs'],
  '& .MuiTab-root': {
    ...typography['text-xs-semibold'],
    py: backrs.spacing['spacing-xs'],
    px: backrs.spacing['spacing-md'],
    marginRight: backrs.spacing['spacing-xs'],
  },
});

const donutRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
  marginTop: backrs.spacing['spacing-xs'],
  '@container (min-width: 600px)': {
    gap: backrs.spacing['spacing-2xl'],
  },
  '@container (min-width: 900px)': {
    gridTemplateColumns: `auto minmax(0, 1fr) ${backrs.width['width-xxs']}`,
  },
});

/** Column-major so grouped categories read top-to-bottom, not across. */
const legendSx =
  (rows: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    columnGap: backrs.spacing['spacing-lg'],
    rowGap: backrs.spacing['spacing-xs'],
    alignContent: 'start',
    width: '100%',
    '@container (min-width: 600px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gridTemplateRows: `repeat(${rows}, auto)`,
      gridAutoFlow: 'column',
      rowGap: backrs.spacing['spacing-sm'],
    },
  });

const legendItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  minWidth: 0,
  overflowWrap: 'anywhere',
});

const legendSwatchSx =
  (color: DegreeMix['categories'][number]['category']['color']): SxProps<Theme> =>
  ({ backrs }) => ({
    flex: '0 0 auto',
    width: backrs.spacing['spacing-md'],
    height: backrs.spacing['spacing-md'],
    borderRadius: backrs.radius['radius-xs'],
    backgroundColor: backrs.colors[color],
  });

const kpiRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  '@container (min-width: 600px)': {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },
});

const statCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  flex: '1 0 0',
  minWidth: 0,
  padding: backrs.spacing['spacing-2xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xs'],
});

const statValueRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
  minWidth: 0,
});

const statBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const kpiCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xs'],
  overflow: 'hidden',
});

const kpiCardBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  flex: '1 0 auto',
  padding: backrs.spacing['spacing-2xl'],
});

const kpiCardFooterSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
  alignSelf: 'stretch',
  padding: `${backrs.spacing['spacing-xl']} ${backrs.spacing['spacing-2xl']}`,
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const kpiCardFooterTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const infoBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const bulletListSx: SxProps<Theme> = ({ backrs }) => ({
  margin: 0,
  marginTop: backrs.spacing['spacing-xs'],
  paddingLeft: backrs.spacing['spacing-2xl'],
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const primaryTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const tertiaryTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
